import { useEffect, useMemo, useState } from 'react';
import { NavLink, generatePath, useMatch, useParams } from 'react-router-dom';
import { PermissionEnum, Table, useQueryState } from '@ui/src';
import { useAppSelector } from '@store';
import Card from '../../../components/partial/card/card';
import { getCofStatus } from '../../../components/corporate/overview/helpers';
import {
  CofStatus,
  Company,
  CustomerStatus,
  SignatureType,
} from '../../customers/types';
import {
  overviewCompaniesSchema,
  overviewCompaniesColumns,
} from '../variables/overview-companies';
import { OverviewCompaniesTypes } from './types';

import cn from '../information.module.css';
import cardStyles from '../../../components/partial/card/card.module.css';

import ROUTES from '../../../routes';

const getCofValues = ({
  type,
  signatureType,
  accessRight,
}: {
  type: string;
  signatureType: SignatureType;
  accessRight: string;
}) => {
  let cofType: string;
  let cofSignatureType: string;
  let cofAccessRight: string;

  switch (type) {
    case 'MAIN_REPRESENTATIVE':
      cofType = 'Main Representative';
      break;
    case 'COMPANY_OFFICIAL':
      cofType = 'Company official';
      break;
    default:
      cofType = type;
      break;
  }

  switch (signatureType) {
    case SignatureType.NONE:
      cofSignatureType = 'None';
      break;
    case SignatureType.SINGLE:
      cofSignatureType = 'Single';
      break;
    default:
      cofSignatureType = type;
      break;
  }

  switch (accessRight) {
    case 'NONE':
      cofAccessRight = 'None';
      break;
    case 'VIEWER':
      cofAccessRight = 'Viewer';
      break;
    default:
      cofAccessRight = type;
      break;
  }

  return { cofType, cofAccessRight, cofSignatureType };
};

const formatCompaniesList = (list: Company[]): Company[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const { cofAccessRight, cofSignatureType, cofType } = getCofValues({
      type: item.companyOfficialType,
      signatureType: item.signatureType,
      accessRight: (item?.accessRight as string) || 'NONE',
    });

    const { cofStatus, cofStatusClassName } = getCofStatus(
      result.status as CofStatus,
    );

    const render: Record<string, React.JSX.Element> = {
      organizationId: <div>{item.organization.companyName}</div>,
      companyOfficialType: (
        <div>
          {cofType}: <span className={cofStatusClassName}>{cofStatus}</span>
        </div>
      ),
      signatureType: <div>{cofSignatureType}</div>,
      accessRight: <div>{cofAccessRight}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const OverviewCompanies = ({
  isLoading,
  status,
}: OverviewCompaniesTypes): React.JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<'current' | 'removed'>(
    'current',
  );
  const [selectedCompanies, setSelectedCompanies] = useState<Company[]>([]);

  const { customerId } = useParams();

  const { baseUserInformation } = useAppSelector(
    (state) => state.individualCustomers.info.overview,
  );
  const { activePermissions } = useAppSelector((state) => state.root);

  const { sort, onSortChange, onFilterChange, filters } = useQueryState(
    overviewCompaniesSchema,
  );

  const transactionsMatch = useMatch(
    generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_TRANSACTIONS, {
      customerId: customerId as string,
    }),
  );

  const formattedData = useMemo(() => {
    const data = formatCompaniesList(
      baseUserInformation?.representativeOfOrganizations as unknown as Company[],
    );

    return data;
  }, [baseUserInformation?.representativeOfOrganizations]);

  useEffect(() => {
    if (formattedData) {
      if (selectedTab === 'current') {
        setSelectedCompanies(
          formattedData.filter(
            (item) =>
              item.status === CofStatus.APPROVED ||
              item.status === CofStatus.PENDING_REVIEW,
          ),
        );
      } else {
        setSelectedCompanies(
          formattedData.filter(
            (item) =>
              item.status !== CofStatus.APPROVED &&
              item.status !== CofStatus.PENDING_REVIEW,
          ),
        );
      }
    }
  }, [formattedData, selectedTab]);

  let companiesPath = generatePath(
    ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_CARDS_COMPANIES,
    { customerId: customerId as string },
  );

  if (transactionsMatch) {
    companiesPath = generatePath(
      ROUTES.CUSTOMERS_INDIVIDUAL_OVERVIEW_COMPANIES,
      { customerId: customerId as string },
    );
  }

  return (
    <div>
      <div className={cn.companiesTitle}>
        <div className={cn.companiesTitleText}>Companies</div>
        {activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_EDIT_OFFICIALS_ACTION,
        ) &&
          status !== CustomerStatus.CLOSED && (
            <NavLink to={companiesPath} className={cn.companiesTitleLink}>
              Edit
            </NavLink>
          )}
      </div>
      <div className={cn.companyOfficialTabs}>
        <button
          type="button"
          className={`${cn.companyOfficialTab} ${
            selectedTab === 'current' ? cn.companyOfficialTabActive : ''
          }`}
          onClick={() => setSelectedTab('current')}
        >
          Current
        </button>
        <button
          type="button"
          className={`${cn.companyOfficialTab} ${
            selectedTab === 'removed' ? cn.companyOfficialTabActive : ''
          }`}
          onClick={() => setSelectedTab('removed')}
        >
          Removed
        </button>
      </div>

      <Card
        isLoading={isLoading}
        className={`${cardStyles.overviewCompaniesCard} ${cn.overviewCompaniesCard}`}
      >
        <Table
          columns={overviewCompaniesColumns}
          data={selectedCompanies || []}
          onSort={(name) => onSortChange(name)}
          filters={filters}
          sort={sort}
          columnsLabels={{}}
          onFilterChange={onFilterChange}
          isLoading={isLoading}
          tableFormClassName={cn.overviewCompaniesTableForm}
          tableHeadCellClassName={cn.overviewCompaniesTableHeadCell}
          tableBodyClassName={
            !selectedCompanies?.length ? cn.overviewCompaniesTableBody : ''
          }
          clickable
        />
      </Card>
    </div>
  );
};

export default OverviewCompanies;
